import { Option, Result } from "@cartographerio/fp";
import { IO } from "@cartographerio/io";
import {
  AccessToken,
  ApiConfig,
  ApiParams,
  checkedToResult,
  CheckResetPasswordTokenResponse,
  Credentials,
  ForgotPasswordRequest,
  isChecked,
  isCheckResetPasswordTokenResponse,
  isCredentials,
  isLoginResponse,
  isRoleV2,
  LoginRequest,
  LoginResponse,
  Message,
  ResetPasswordRequest,
  ResetPasswordToken,
  RoleV2,
} from "@cartographerio/types";
import * as fetch from "../../fetch";
import {
  contentAs,
  optionalContentAs,
  recoverUnknownErrors,
  validationErrorsToChecked,
} from "../../response";
import { UrlParts } from "../../url";

const basePath = "/auth/v2";

export function login(
  apiConfig: ApiConfig,
  body: LoginRequest
): IO<Result<Message[], LoginResponse<RoleV2>>> {
  const url: UrlParts = { path: `${basePath}/login` };
  return fetch
    .post({ apiParams: { apiConfig }, url, body })
    .map(
      recoverUnknownErrors({
        400: isLoginResponse(isRoleV2),
        401: isLoginResponse(isRoleV2),
        403: isLoginResponse(isRoleV2),
        404: isLoginResponse(isRoleV2),
      })
    )
    .map(validationErrorsToChecked)
    .flatMap(
      contentAs(
        "Checked<LoginResponse<RoleV2>>",
        isChecked(isLoginResponse(isRoleV2))
      )
    )
    .map(checkedToResult);
}

export function readOrFail(
  apiParams: ApiParams,
  token: AccessToken
): IO<Credentials<RoleV2>> {
  return fetch
    .get({ apiParams, url: { path: `${basePath}/${token}` } })
    .flatMap(contentAs("CredentialsV2", isCredentials(isRoleV2)));
}

export function readOption(
  apiParams: ApiParams,
  token: AccessToken
): IO<Option<Credentials<RoleV2>>> {
  return fetch
    .get({ apiParams, url: { path: `${basePath}/${token}` } })
    .flatMap(optionalContentAs("CredentialsV2", isCredentials(isRoleV2)));
}

export function forgotPassword(
  apiParams: ApiParams,
  request: ForgotPasswordRequest
): IO<void> {
  return fetch
    .post({
      apiParams,
      url: { path: `${basePath}/forgot-password` },
      body: request,
    })
    .void();
}

export function checkResetToken(
  apiParams: ApiParams,
  token: ResetPasswordToken
): IO<CheckResetPasswordTokenResponse> {
  return fetch
    .get({ apiParams, url: { path: `${basePath}/check-token/${token}` } })
    .flatMap(
      contentAs(
        "CheckResetPasswordTokenResponse",
        isCheckResetPasswordTokenResponse
      )
    );
}

export function resetPassword(
  apiParams: ApiParams,
  request: ResetPasswordRequest
): IO<CheckResetPasswordTokenResponse> {
  return fetch
    .post({
      apiParams,
      url: { path: `${basePath}/reset-password` },
      body: request,
    })
    .flatMap(
      contentAs(
        "CheckResetPasswordTokenResponse",
        isCheckResetPasswordTokenResponse
      )
    );
}
